import React, { useEffect } from "react";
import Link from "next/link";
import Head from "next/head";
import { useRouter } from "next/router";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";

import BlogImage from "components/customImages/BlogImage";
import { getCurrentPath, getOrigin } from "utils/common";
import Button from "components/ui_components/button";
import { BASE_URL } from "helpers/constants/routes";

function Index() {
  const router = useRouter();
  const { locale } = router;

  useEffect(() => {
    document.documentElement.dir = "ltr";
  }, [locale]);
  const origin = getOrigin();
  const currentPath = getCurrentPath();
  return (
    <>
      <Head>
        <title>{"  Découverte de la gastronomie tunisienne et ses bonnes adresses"}</title>
        <meta
          name="description"
          content="Explorez la richesse de la gastronomie tunisienne : plats traditionnels, douceurs locales et les meilleures adresses où savourer ces délices en Tunisie."
        />
        <link rel="alternate" hrefLang="fr" href={`${origin}${currentPath}`} />
        <link rel="alternate" hrefLang="ar" href={`${origin}/ar${currentPath}`} />
      </Head>
      <div className="flex flex-col  w-full py-10 justify-center">
        <div className="max-w-[680px] m-auto px-6">
          <h1 className="font-bold text-md lg:text-lg  mb-10 text-center ">{`La gastronomie en Tunisie : Un voyage culinaire à travers la tradition et la modernité`}</h1>
          <time dateTime="02-04-2025">Mai 25, 2025</time>
          <p>
            {`La Tunisie est un véritable joyau culinaire. Sa gastronomie, riche et diversifiée, reflète son histoire, sa culture, et son patrimoine méditerranéen. Entre épices, plats traditionnels et douceurs sucrées, chaque bouchée est une invitation au voyage. Ce guide explore les trésors gastronomiques de la Tunisie et vous recommande les meilleurs endroits pour les savourer.`}
          </p>
          <div className="flex justify-center p-5">
            <BlogImage
              imageName="top-view-cooked-vegetables-such-as-red-tomatoes-green-bell-pepper-black-eggplants-potatoes-inside-plate-plate.jpg"
              alt="La gastronomie en Tunisie"
              className="object-contain"
              imageUrl="top-view-cooked-vegetables-such-as-red-tomatoes-green-bell-pepper-black-eggplants-potatoes-inside-plate-plate.jpg"
            />
          </div>

          {/*  -------- First section ----------- */}
          <h3 className="font-bold text-md lg:text-2xl">{`I. Les bases de la cuisine tunisienne`}</h3>
          <h3 className="font-bold text-md lg:text-lg m-2">{`1. Une cuisine méditerranéenne aux multiples influences`}</h3>

          <p>
            La cuisine tunisienne s&apos;inspire des traditions berbères, arabes, andalouses,
            ottomanes et françaises. Ce mélange unique donne naissance à des plats authentiques et
            savoureux. L&apos;huile d&apos;olive, les épices, les légumes frais, les céréales et les
            produits de la mer occupent une place centrale.{" "}
          </p>
          <h3 className="font-bold text-md lg:text-lg m-2">{`2. Les ingrédients incontournables`}</h3>

          <br />
          <ul className=" ps-10 list-disc">
            <li>
              <p>
                <span className="font-bold">L&apos;harissa : </span>{" "}
                {`Une pâte de piment rouge relevée, emblématique des saveurs tunisiennes.`}
              </p>
            </li>
            <li>
              <p>
                <span className="font-bold">Les épices : </span>{" "}
                {` Cumin, coriandre, curcuma et carvi enrichissent les plats de leurs arômes uniques.`}
              </p>
            </li>
            <li>
              <p>
                <span className="font-bold">Les produits locaux : </span>{" "}
                {` Couscous, dattes de Deglet Nour, poissons de Méditerranée, et fruits gorgés de soleil.`}
              </p>
            </li>
          </ul>

          <h3 className="font-bold text-md lg:text-2xl">{`II. Les plats traditionnels à découvrir absolument`}</h3>
          <h3 className="font-bold text-md lg:text-lg m-2">{`1. Le couscous`}</h3>
          <p>
            Le couscous est un plat national tunisien. Préparé avec semoule, légumes, pois chiches
            et viande (agneau, poulet ou poisson), il est souvent accompagné d&apos;une sauce épicée
            appelée &quot;marka&quot;.
          </p>
          <h3 className="font-bold text-md lg:text-lg m-2">{`2. La brik`}</h3>
          <p>
            La brik est une fine feuille de pâte croustillante, garnie traditionnellement
            d&apos;œuf, de thon et de persil. Un délice incontournable en entrée ou comme snack.
          </p>
          <h3 className="font-bold text-md lg:text-lg m-2">{`3. Le tajine tunisien`}</h3>
          <p>
            Contrairement au tajine marocain, le tajine tunisien ressemble davantage à une quiche.
            Préparé avec des œufs, du fromage, de la viande et des épices, il se déguste chaud ou
            froid.
          </p>
          <h3 className="font-bold text-md lg:text-lg m-2">{`4. Les poissons et fruits de mer`}</h3>
          <p>
            Les villes côtières tunisiennes comme La Goulette et Sfax offrent des spécialités de
            poissons grillés ou en ragoût. Le &quot;poisson à la chermoula&quot;, un plat de poisson
            mariné, est particulièrement apprécié.
          </p>
          <h3 className="font-bold text-md lg:text-lg m-2">{`5. Les douceurs sucrées`}</h3>
          <ul className="ps-10 list-disc">
            <li>
              {" "}
              <span className="font-bold">Makroudh : </span>Un gâteau à base de semoule et de pâte
              de dattes, imbibé de miel.
            </li>
            <li>
              {" "}
              <span className="font-bold">Bambalouni : </span>Une sorte de beignet sucré frit,
              populaire dans les ruelles de Sidi Bou Saïd.
            </li>
            <li>
              {" "}
              <span className="font-bold">Zlabia et chamya :</span>Idéales pour accompagner un café
              ou un thé à la menthe.
            </li>
          </ul>

          <div className="flex justify-center p-5">
            <BlogImage
              imageName="couscous-1503943_1280.jpg"
              alt=" Les plats traditionnels en Tunisie"
              className="object-contain"
              imageUrl="couscous-1503943_1280.jpg"
            />
          </div>
          <h3 className="font-bold text-md lg:text-2xl">{`III. Où manger en Tunisie ? Les meilleures adresses à ne pas manquer`}</h3>
          <h3 className="font-bold text-md lg:text-lg m-2">{`1. Tunis et ses environs`}</h3>

          <ul className=" ps-10 list-disc">
            <li>
              <p>
                <span className="font-bold">
                  <a
                    className="text-blue underline visited:text-[#0369a1]"
                    href={`${BASE_URL}categories/gastronomie/facebook-lemdina-lmdynt`}
                  >
                    Lemdina
                  </a>{" "}
                  (Médina de Tunis) :
                </span>{" "}
                Ce restaurant traditionnel propose une cuisine raffinée dans un cadre authentique.
                Leur couscous et méchoui sont particulièrement appréciés.{" "}
              </p>
            </li>
            <li>
              <p>
                <span className="font-bold">Chez Slah (Centre-ville) : </span>{" "}
                {` Une adresse incontournable pour les amateurs de poisson frais.`}
              </p>
            </li>
            <li>
              <p>
                <span className="font-bold">La Goulette : </span>{" "}
                {`Ce quartier est connu pour ses restaurants de poissons, comme Le `}{" "}
                <span className="font-bold">Pirate</span> ou{" "}
                <span className="font-bold">Le Golfe.</span>
              </p>
            </li>
          </ul>

          <h3 className="font-bold text-md lg:text-lg m-2">{`2. Sidi Bou Saïd`}</h3>
          <ul className=" ps-10 list-disc">
            <li>
              <p>
                <span className="font-bold">Café des Délices : </span>{" "}
                {`En plus d'une vue imprenable sur la mer, dégustez des pâtisseries tunisiennes et du thé à la menthe.`}
              </p>
            </li>
            <li>
              <p>
                <span className="font-bold">
                  <a
                    className="text-blue underline visited:text-[#0369a1]"
                    href={`${BASE_URL}categories/gastronomie/instagram-dar-zarrouk-officiel`}
                  >
                    Dar Zarrouk :{" "}
                  </a>
                </span>{" "}
                {`Idéal pour un dîner romantique, ce restaurant sert des plats méditerranéens revisités.`}
              </p>
            </li>
          </ul>
          <h3 className="font-bold text-md lg:text-lg m-2">{`3. Hammamet`}</h3>

          <ul className=" ps-10 list-disc">
            <li>
              <p>
                <span className="font-bold">Le Barberousse : </span>{" "}
                {`Situé au bord de la mer, il propose une cuisine tunisienne et internationale dans un cadre enchanteur.`}
              </p>
            </li>
            <li>
              <p>
                <span className="font-bold">La Bella Marina : </span>{" "}
                {`Parfait pour les fruits de mer et les poissons.`}
              </p>
            </li>
          </ul>

          <h3 className="font-bold text-md lg:text-lg m-2">{`4. Sousse et Monastir`}</h3>

          <ul className=" ps-10 list-disc">
            <li>
              <p>
                <span className="font-bold">Restaurant L&apos;Escargot (Sousse) :</span>{" "}
                {` Une adresse élégante pour des plats locaux revisités.`}
              </p>
            </li>
            <li>
              <p>
                <span className="font-bold">Ali Baba (Monastir) : </span>{" "}
                {`Spécialisé dans les grillades et poissons.`}
              </p>
            </li>
          </ul>

          <h3 className="font-bold text-md lg:text-lg m-2">{`5. Djerba`}</h3>

          <ul className=" ps-10 list-disc">
            <li>
              <p>
                <span className="font-bold">Restaurant de l&apos;Île :</span>{" "}
                {`Connue pour ses couscous et ses plats de poulpe.`}
              </p>
            </li>
            <li>
              <p>
                <span className="font-bold">Chez Mami : </span>{" "}
                {`Une adresse locale où déguster des plats faits maison dans une ambiance conviviale.`}
              </p>
            </li>
          </ul>

          <div className="flex justify-center m-8">
            <Button>
              <Link
                target="_blank"
                rel="noopener"
                href={`${BASE_URL}categories/gastronomie?extraOptions=%7B%22sub_category%22%3A%7B%22name%22%3A%7B%22containsi%22%3A%22Gas+%3E+restaurants%22%7D%7D%2C%22platform%22%3A%7B%22in%22%3A%5B%22FACEBOOK%22%5D%7D%2C%22reviewRate%22%3A%7B%22in%22%3A%5B%22R_5%22%5D%7D%2C%22client_reviews%22%3A%7B%22general_review%22%3A%7B%22in%22%3A%5B5%5D%7D%7D%7D`}
                passHref
                legacyBehavior
              >
                {`Découvrez les restaurants les plus notés sur Facebook`}
              </Link>
            </Button>
          </div>

          <h3 className="font-bold text-md lg:text-xl m-2">{`IV. Les expériences culinaires à vivre`}</h3>
          <h3 className="font-bold text-md lg:text-lg m-2">{`1. Les marchés traditionnels`}</h3>
          <p>
            Les marchés tunisiens sont un véritable spectacle de couleurs et de saveurs. À Tunis,
            visitez le marché central pour ses étals de fruits, légumes, épices et poissons frais.
          </p>
          <h3 className="font-bold text-md lg:text-lg m-2">{`2. Cuisiner comme un local`}</h3>
          <p>
            Participez à un atelier culinaire pour apprendre à préparer des plats tunisiens. Ces
            expériences sont souvent proposées dans des riads ou des maisons d&apos;hôtes.
          </p>
          <h3 className="font-bold text-md lg:text-lg m-2">{`3. Les festivals gastronomiques`}</h3>

          <ul className=" ps-10 list-disc">
            <li>
              <p>
                <span className="font-bold">
                  Festival de l&apos;harissa et du piment à Nabeul :
                </span>{" "}
                {`Une célébration de l'ingrédient star de la cuisine tunisienne.`}
              </p>
            </li>
            <li>
              <p>
                <span className="font-bold">Festival des dattes à Tozeur : </span>{" "}
                {`Une occasion de goûter les meilleures dattes et leurs dérivés.`}
              </p>
            </li>
          </ul>
          <h3 className="font-bold text-md lg:text-xl m-2">{`V. Conseils pour une expérience culinaire réussie`}</h3>
          <ul className=" ps-10 list-disc">
            <li>
              <p>
                <span className="font-bold">Explorez les petits restaurants locaux :</span>{" "}
                {` Les gargotes et petits établissements offrent souvent les saveurs les plus authentiques à des prix abordables.`}
              </p>
            </li>
            <li>
              <p>
                <span className="font-bold">Adaptez-vous au niveau d&apos;épices :</span>{" "}
                {`La cuisine tunisienne peut être très relevée. N'hésitez pas à demander un plat moins épicé si nécessaire.`}
              </p>
            </li>
            <li>
              <p>
                <span className="font-bold">Goûtez le thé tunisien : </span>{" "}
                {`Parfumé à la menthe ou aux pignons de pin, il accompagne parfaitement les douceurs locales.`}
              </p>
            </li>
          </ul>
          <h3 className="font-bold text-md lg:text-xl m-2">{`Conclusion`}</h3>
          <p>
            La gastronomie tunisienne est bien plus qu&apos;un simple plaisir pour les papilles :
            c&apos;est une plongée dans l&apos;âme d&apos;un pays riche en histoire et en
            traditions. Que vous dégustiez un couscous dans un restaurant étoilé ou une brik
            croustillante dans une petite échoppe, chaque repas est une célébration des saveurs.
            Explorez les marchés, goûtez les spécialités locales et laissez-vous envoûter par la
            générosité de cette cuisine méditerranéenne. Bon appétit !
          </p>
        </div>
      </div>
    </>
  );
}

export async function getStaticProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["layout", "categories", "signup_promotion"])),
    },
  };
}

export default Index;
